
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import CollectionGrid from "@/components/dashboard/CollectionGrid";
import { useCollections } from "@/contexts/collections";
import type { Collection as CollectionType } from "@/contexts/collections/types";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { usePersistentViewMode } from "@/hooks/usePersistentViewMode";
import EditContentDialog from "@/components/dashboard/EditContentDialog";
import { useMetadata } from "@/hooks/useMetadata";
import { APP_METADATA } from "@/constants/metadata";

// Define the interface for tag selection data
interface TagSelectionData {
  tagIds: string[];
  logicType: "AND" | "OR";
}

const Collection = () => {
  const { id } = useParams<{ id: string }>();
  const { collections, refreshCollections } = useCollections();
  const { user } = useAuth();
  const { viewMode, setViewMode } = usePersistentViewMode();
  const [selectedContentId, setSelectedContentId] = useState<string | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [currentTitle, setCurrentTitle] = useState("");
  
  // State for selected tags
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [tagLogicType, setTagLogicType] = useState<"AND" | "OR">("AND");
  const [isLoading, setIsLoading] = useState(true);
  
  // First, define the collection query
  const { data: collection, isLoading: isCollectionLoading, refetch } = useQuery({
    queryKey: ['collection', id, user?.id],
    queryFn: async () => {
      if (!id || !user) return null;
      
      console.log("Fetching collection data for ID:", id);
      
      // ALWAYS fetch from database to ensure fresh data
      try {
        const { data, error } = await supabase
          .from('collections')
          .select('*')
          .eq('id', id)
          .eq('user_id', user.id)
          .single();
        
        if (error) {
          console.error('Error fetching collection:', error);
          toast.error('Failed to load collection');
          return null;
        }
        
        // For debugging
        console.log("Fetched collection from DB:", data?.name, "description:", data?.description, "color:", data?.color);
        return data as CollectionType;
      } catch (error: any) {
        console.error('Error fetching collection:', error);
        return null;
      }
    },
    enabled: !!id && !!user,
    staleTime: 0, // Never use stale data
    refetchOnMount: 'always', // Always refetch when component mounts
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchInterval: false
  });
  
  // Now use collection after it's declared
  // Get metadata from constants and apply using the hook
  const { title, description, image } = APP_METADATA.default;
  useMetadata({ 
    title: collection?.name ? `${collection.name} - Clustr` : title,
    description: collection?.description || description,
    image 
  });
  
  // Force collection refresh when component mounts or ID changes
  useEffect(() => {
    console.log("Collection component mounted or id changed, refreshing collections data for ID:", id);
    refreshCollections();
    
    // Check for selected tags in localStorage
    const storedTags = localStorage.getItem('selectedTags');
    if (storedTags) {
      try {
        const parsedData = JSON.parse(storedTags) as TagSelectionData;
        
        // Ensure we have valid arrays and default values if needed
        setSelectedTagIds(Array.isArray(parsedData.tagIds) ? parsedData.tagIds : []);
        setTagLogicType(parsedData.logicType || "AND");
        
        console.log("Loaded selected tags from storage:", parsedData);
      } catch (error) {
        console.error("Error parsing stored tags:", error);
        // Reset to defaults if there's an error
        setSelectedTagIds([]);
        setTagLogicType("AND");
      }
    }
  }, [id, refreshCollections]);
  
  // Listen for changes in selected tags
  useEffect(() => {
    const handleCustomEvent = (e: Event) => {
      if (e instanceof CustomEvent && e.detail) {
        try {
          const data = e.detail as TagSelectionData;
          console.log("Collection received tag selection change event:", data);
          setSelectedTagIds(Array.isArray(data.tagIds) ? data.tagIds : []);
          setTagLogicType(data.logicType || "AND");
          
          // Also save to localStorage for persistence
          localStorage.setItem('selectedTags', JSON.stringify({
            tagIds: Array.isArray(data.tagIds) ? data.tagIds : [],
            logicType: data.logicType || "AND"
          }));
        } catch (error) {
          console.error("Error handling custom event:", error);
        }
      }
    };
    
    window.addEventListener('selectedTagsChanged', handleCustomEvent as EventListener);
    
    return () => {
      window.removeEventListener('selectedTagsChanged', handleCustomEvent as EventListener);
    };
  }, []);
  
  // Update loading state
  useEffect(() => {
    setIsLoading(isCollectionLoading);
  }, [isCollectionLoading]);
  
  // Force refetch when ID changes
  useEffect(() => {
    if (id) {
      console.log("ID changed, forcing refetch for collection:", id);
      refetch();
    }
  }, [id, refetch]);
  
  const handleEditContent = (contentId: string, title: string) => {
    setSelectedContentId(contentId);
    setCurrentTitle(title);
    setEditDialogOpen(true);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin h-8 w-8 border-4 border-clustr-blue border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col h-full">
      <main className="flex-1 overflow-y-auto h-full">
        {collection ? (
          <CollectionGrid 
            title={collection.name}
            description={collection.description || ""}
            collectionId={collection.id}
            collectionColor={collection.color}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            onEditContent={handleEditContent}
            selectedTagIds={selectedTagIds}
            tagLogicType={tagLogicType}
          />
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <h1 className="text-2xl font-bold text-clustr-darkGray">Collection not found</h1>
            <p className="text-clustr-mediumGray mt-2">
              The collection you're looking for doesn't exist or you don't have access to it.
            </p>
          </div>
        )}
        
        {selectedContentId && (
          <EditContentDialog
            contentId={selectedContentId}
            title={currentTitle}
            isOpen={editDialogOpen}
            onOpenChange={setEditDialogOpen}
          />
        )}
      </main>
    </div>
  );
};

export default Collection;
